import * as React from 'react';
import { useEffect, useState } from 'react';

import { flatten } from 'lodash';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { accountGroupsForUsernameSelector } from '../../account-group/account-group-selectors';
import { loadAccountGroups } from '../../account-group/actions/load-account-groups';
import { moveJobApplicant } from '../../action/application/move-job-applicant';
import { loadOrgs } from '../../action/load-orgs';
import { loadAccountJobs } from '../../job/actions/load-account-jobs';
import { fullApplicationSelector } from '../../reducers/application-selectors';
import { userSelector } from '../../reducers/current-user-selectors';
import { accountJobsSelector } from '../../reducers/job-selectors';
import { orgsForAccountId } from '../../reducers/org-selectors';

import { UiAutocomplete } from '../../ui/ui-autocomplete';
import { UiButton } from '../../ui/ui-button';
import { UiDialog } from '../../ui/ui-dialog';

import { groupJobsByLocation } from '../../util/group-jobs-by-location';
import { isNonNullable } from '../../util/is-truthy';
import { sortJobs } from '../../util/sort-functions';
import { LoadingMessage } from '../../widgets/loading-message';
import { SingleLanguageMessageComposer } from '../../widgets/message-composer/wrappers/single-language-message-composer';

const mapStateToProps = (state: any, ownProps: InputProps) => {
  const user = userSelector(state);
  const accountGroups = accountGroupsForUsernameSelector(state, user.username).flatMap(
    (ag) => ag.Accounts,
  );

  let activeOrgs = orgsForAccountId(state, ownProps.accountId);
  let jobs = accountJobsSelector(state, ownProps.accountId);

  if (accountGroups.length) {
    // If user is part of an account group, combine the orgs for each of the accounts into one list. Do the same for the jobs
    const selectOrgsForAccountGroup = accountGroups
      .map((group) => orgsForAccountId(state, group.accountId))
      .filter(isNonNullable);

    activeOrgs = flatten(selectOrgsForAccountGroup);

    const selectJobsForAccountGroup = accountGroups
      .map((group) => accountJobsSelector(state, group.accountId))
      .filter(isNonNullable);

    jobs = flatten(selectJobsForAccountGroup);
  }

  return {
    accountGroups,
    activeOrgs,
    jobApplicant: fullApplicationSelector(state, ownProps.jobApplicantId),
    jobs,
    user,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    loadAccountJobs: (accountId: number) => dispatch(loadAccountJobs(accountId)),
    loadAccountGroups: (username: string) => dispatch(loadAccountGroups(username)),
    moveJobApplicant: (jobId: number, message: string | null) =>
      dispatch(moveJobApplicant(ownProps.jobApplicantId, jobId, message)),
    loadOrgs: (accountId: number) => dispatch(loadOrgs(accountId)),
  },
});

type InputProps = {
  accountId: number;
  jobApplicantId: number;
  onClose: () => void;
  onMovedApplicant: Function;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const MoveApplicantDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    accountGroups,
    accountId,
    actions,
    activeOrgs,
    jobApplicant,
    jobs,
    onMovedApplicant,
    onClose,
    user,
  }: Props) => {
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(jobApplicant.orgId);
    const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
    const [sendMessage, setSendMessage] = useState<boolean>(true);
    const [messageToApplicant, setMessageToApplicant] = useState<string>('');

    useEffect(() => {
      if (!accountGroups.length) {
        // User is not part of an account group. Load the orgs and jobs for the current account
        actions.loadOrgs(accountId);
        actions.loadAccountJobs(accountId);
      } else {
        // User is part of an account group. Load the orgs and jobs for each of the accounts user has access to
        accountGroups.map(async (account) => {
          actions.loadOrgs(account.accountId);
          actions.loadAccountJobs(account.accountId);
        });
      }
    }, [accountGroups, accountId, actions]);

    useEffect(() => {
      if (user.accountGroupId) {
        actions.loadAccountGroups(user.username);
      }
    }, [actions, user]);

    const handleOrgSelected = (value: string) => {
      const newSelectedOrgId = parseInt(value as string, 10);

      setSelectedOrgId(newSelectedOrgId);
      setSelectedJobId(null);
      setMessageToApplicant('');
    };

    const handleJobSelected = (value: string) => {
      const newSelectedJobId = parseInt(value as string, 10);
      let newMessageToApplicant = '';
      if (jobs && jobApplicant) {
        const oldJob = jobs?.find((j: Job) => j.job_id === jobApplicant.job_id);
        const newJob = jobs?.find((j: Job) => j.job_id === newSelectedJobId);
        if (oldJob && newJob) {
          newMessageToApplicant = `Hi ${
            jobApplicant.firstName
          }! Thanks for your application to be a ${
            oldJob.title
          }. Because of your application and our needs, we think you'd be a better fit for our job, ${
            newJob.title || ''
          }. If you don't want to be considered, please let us know by responding to this message.`;
        }
      }

      setSelectedJobId(newSelectedJobId);
      setMessageToApplicant(newMessageToApplicant);
    };

    const handleMessageToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSendMessage(event.target.checked);
    };

    const handleMessageUpdate = (message: string) => {
      setMessageToApplicant(message);
    };

    const handleMoveApplicantToJob = () => {
      if (!selectedJobId) {
        return;
      }

      actions.moveJobApplicant(selectedJobId, sendMessage ? messageToApplicant : null);
      onMovedApplicant();
    };

    if (!jobs) {
      return <LoadingMessage />;
    }

    const orgOptions =
      activeOrgs?.map((org) => ({
        label: org.name,
        value: org.org_id.toString(),
      })) || [];

    const jobsForSelectedOrg = jobs.filter((job) => job.orgId === selectedOrgId);

    const jobOptions: Array<SelectOption<string>> = [];

    const jobsByLocation = jobsForSelectedOrg.length ? groupJobsByLocation(jobsForSelectedOrg) : {};
    const hasMoreThanOneLocation = Object.keys(jobsByLocation).length > 1;

    Object.keys(jobsByLocation).forEach((location) => {
      const jobsAtLocation = jobsByLocation[location].jobs.sort(sortJobs);

      jobsAtLocation.forEach((job) =>
        jobOptions.push({
          value: job.job_id.toString(),
          label: job.title,
          ...(hasMoreThanOneLocation && { groupBy: location }),
        }),
      );
    });

    const jobOptionText = jobOptions.length
      ? 'Select the job'
      : "This organization doesn't have any jobs";

    return (
      <UiDialog onClose={onClose} title="Move this applicant to a new job" size="xs">
        <UiDialog.Content>
          <div className="u-width-100">
            {orgOptions?.length > 1 ? (
              <div className="u-margin-bottom-lg">
                <UiAutocomplete
                  value={selectedOrgId?.toString() || ''}
                  options={orgOptions}
                  onChange={handleOrgSelected}
                  label="Select an Organization"
                />
              </div>
            ) : null}
            <UiAutocomplete
              value={selectedJobId?.toString() || ''}
              options={jobOptions}
              onChange={handleJobSelected}
              label={jobOptionText}
              disabled={!jobOptions.length}
              dataCy="move-applicant-job-selector"
            />
            {selectedJobId === null ? null : (
              <div>
                <FormControlLabel
                  control={<Checkbox checked={sendMessage} onChange={handleMessageToggle} />}
                  label="Send applicant message about job change"
                />
                {sendMessage ? (
                  <SingleLanguageMessageComposer
                    onChange={handleMessageUpdate}
                    message={messageToApplicant}
                    enableEmail={false}
                    enableText
                  />
                ) : null}
              </div>
            )}
          </div>
        </UiDialog.Content>
        <UiDialog.Actions>
          <UiButton variant="dismiss" onClick={onClose}>
            Cancel
          </UiButton>
          <UiButton disabled={selectedJobId === null} onClick={handleMoveApplicantToJob}>
            Move
          </UiButton>
        </UiDialog.Actions>
      </UiDialog>
    );
  },
);
